import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaUserFriends, FaCalendarAlt, FaEdit, FaPlay, FaClock } from 'react-icons/fa';
import RSVPButtons from './RSVPButtons';
import { useRSVP } from '../hooks/useRSVP';
import { useEventSSE, useLiveEventSSE } from '../hooks/useSSE';
import StartEventModal from './Scorecard/StartEventModal';
import LiveScorecardModal from './Scorecard/LiveScorecardModal';
import FinalResultModal from './Scorecard/FinalResultModal';
import ReadOnlyScorecard from './Scorecard/ReadOnlyScorecard';
import EventDetailsModal from './EventDetailsModal';
import styles from '../pages/EventsPage.module.css';

const EventCard = ({ 
  event, 
  isLoaded, 
  weatherData, 
  onEditEvent,
  onDeleteEvent,
  canManage = false,
  isHighlighted = false,
  programId,
  programDetails,
  onEventUpdated
}) => {
  const rsvpHook = useRSVP(event._id, event);
  const hasAutoOpenedRef = useRef(false);
  
  // SSE for real-time updates
  const eventSSE = useEventSSE(event._id, {
    onRSVPUpdate: () => rsvpHook?.refresh?.(),
    onEventUpdated: (data) => onEventUpdated?.(data.data || data),
    autoConnect: true
  });

  const liveSSE = useLiveEventSSE(event.status === 'Live' ? event._id : null, {
    onLiveUpdate: (data) => onEventUpdated?.(data.data || data),
    autoConnect: event.status === 'Live'
  });
  
  // Modal states
  const [showStartEventModal, setShowStartEventModal] = useState(false);
  const [showLiveScorecardModal, setShowLiveScorecardModal] = useState(false);
  const [showFinalResultModal, setShowFinalResultModal] = useState(false);
  const [showScoreViewer, setShowScoreViewer] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Auto-open details modal when event is highlighted (from URL query param)
  useEffect(() => {
    if (isHighlighted && !hasAutoOpenedRef.current) {
      // Small delay to ensure component is mounted and scrolled into view
      const timer = setTimeout(() => {
        setShowDetailsModal(true);
        hasAutoOpenedRef.current = true;
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted]);

  const { location } = event;
  const coordinatesArray = location?.address?.coordinates?.coordinates;
  const hasValidCoordinates = Array.isArray(coordinatesArray) && coordinatesArray.length >= 2;
  const lat = hasValidCoordinates ? coordinatesArray[1] : null;
  const lng = hasValidCoordinates ? coordinatesArray[0] : null;
  const isValidLatLng = typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng);
  const weather = weatherData[event._id];

  // Format date/time
  const eventDate = new Date(event.dateTime.start);
  const formattedDate = eventDate.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });
  const formattedTime = eventDate.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit' 
  });

  // Get team names
  const homeTeam = event.teams?.home?.name || 'Home';
  const awayTeam = event.teams?.away?.name || event.opponentTeam || 'Away';
  const isPractice = event.type === 'Practice';

  // Score info
  const hasLiveScore = event.status === 'Live' && event.liveSession?.scorecard;
  const hasFinalScore = event.status === 'Completed' && event.finalResults;
  const homeScore = hasFinalScore ? event.finalResults.homeScore : (hasLiveScore ? event.liveSession.scorecard.score.home : null);
  const awayScore = hasFinalScore ? event.finalResults.awayScore : (hasLiveScore ? event.liveSession.scorecard.score.away : null);

  // Handle card click - open details modal
  const handleCardClick = (e) => {
    // Don't open modal if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input')) {
      return;
    }
    setShowDetailsModal(true);
  };

  return (
    <div 
      data-event-id={event._id}
      className={`${styles.eventCard} ${isHighlighted ? styles.highlightedEventCard : ''} ${styles[`card${event.status}`] || ''}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowDetailsModal(true); } }}
      aria-label={`View details for ${event.title}`}
    >
      {/* Mobile Edit Button - Shows when map is hidden */}
      {canManage && onEditEvent && (
        <button 
          className={styles.editBtnMobile} 
          onClick={(e) => { e.stopPropagation(); onEditEvent(event); }} 
          aria-label="Edit Event"
        >
          <FaEdit />
        </button>
      )}

      {/* Main Content - Horizontal Row */}
      <div className={styles.cardBody}>
        {/* Date Column */}
        <div className={styles.dateColumn}>
          <span className={styles.dateDay}>{eventDate.getDate()}</span>
          <span className={styles.dateMonth}>{eventDate.toLocaleDateString('en-US', { month: 'short' })}</span>
          <span className={styles.dateTime}>{formattedTime}</span>
        </div>

        {/* Teams / Event Info */}
        <div className={styles.matchupSection}>
          {/* Event Title */}
          <h3 className={styles.eventTitle}>{event.title}</h3>

          {isPractice ? (
            <div className={styles.practiceHeader}>
              <span className={styles.teamName}>{homeTeam} • {event.type}</span>
            </div>
          ) : (
            <div className={styles.matchup}>
              <div className={styles.team}>
                <span className={styles.teamName}>{homeTeam}</span>
              </div>
              
              <div className={styles.scoreSection}>
                {(hasLiveScore || hasFinalScore) ? (
                  <div className={styles.scoreDisplay}>
                    <span className={styles.score}>{homeScore}</span>
                    <span className={styles.scoreDivider}>:</span>
                    <span className={styles.score}>{awayScore}</span>
                  </div>
                ) : (
                  <div className={styles.vsDisplay}>vs</div>
                )}
              </div>
              
              <div className={styles.team} style={{ textAlign: 'right' }}>
                <span className={styles.teamName}>{awayTeam}</span>
              </div>
            </div>
          )}
          
          {/* Location inline */}
          <div className={styles.locationLine}>
            <FaMapMarkerAlt />
            <span>{location?.name || 'TBD'}</span>
            {weather && (
              <span className={styles.weatherInline}>
                {Math.round(weather.main.temp)}°C
              </span>
            )}
          </div>
        </div>

        {/* Status + Result */}
        <div className={styles.statusColumn}>
          <span className={`${styles.statusLabel} ${styles[`status${event.status}`]}`}>
            {event.status}
            {event.status === 'Live' && <span className={styles.liveDot} />}
          </span>
          {hasFinalScore && event.finalResults.winner && (
            <span className={styles.resultBadge}>
              {event.finalResults.winner === 'tie' ? 'Draw' : 
               event.finalResults.winner === 'home' ? 'Home Win' : 'Away Win'}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className={styles.cardActions}>
          {/* RSVP Section */}
          <div className={styles.rsvpSection}>
            {event.status !== 'Completed' && event.status !== 'Cancelled' && (
              <RSVPButtons
                eventId={event._id}
                event={event}
                userRSVP={rsvpHook.userRSVP}
                summary={rsvpHook.summary}
                onRSVPUpdate={rsvpHook.refresh}
                canRSVP={rsvpHook.canRSVP}
                submitting={rsvpHook.submitting}
                error={rsvpHook.error}
                onSubmit={(response, note) => rsvpHook.submitRSVP(response, note)}
                onClearError={rsvpHook.clearError}
              />
            )}

            {hasFinalScore && (
              <button onClick={() => setShowFinalResultModal(true)} className={styles.actionBtn}>
                📋 Details
              </button>
            )}
          </div>

          {/* Manager Action Buttons - Fixed position */}
          <div className={styles.managerActions}>
            {canManage && (event.status === 'Planned' || event.status === 'Active') && (
              <button onClick={() => setShowStartEventModal(true)} className={styles.startBtn}>
                <FaPlay />
              </button>
            )}
            
            {canManage && event.status === 'Live' && (
              <button onClick={() => setShowLiveScorecardModal(true)} className={styles.manageBtn}>
                🎯
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Map Thumbnail */}
      <div className={styles.mapThumbnail}>
        {/* Edit Button - Floating on map */}
        {canManage && onEditEvent && (
          <button 
            className={styles.editBtnFloating} 
            onClick={(e) => { e.stopPropagation(); onEditEvent(event); }} 
            aria-label="Edit Event"
          >
            <FaEdit />
          </button>
        )}

        {isValidLatLng ? (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.mapLink}
            title={`View ${location?.name || 'location'} on Google Maps`}
          >
            <img
              src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=300x200&markers=color:red%7C${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&style=feature:all%7Celement:labels%7Cvisibility:off&style=feature:road%7Celement:geometry%7Ccolor:0x444444&style=feature:landscape%7Celement:geometry%7Ccolor:0x1a1a1a&style=feature:poi%7Cvisibility:off&style=feature:water%7Celement:geometry%7Ccolor:0x0d0d0d`}
              alt={`Map showing ${location?.name || 'event location'}`}
              className={styles.staticMap}
            />
          </a>
        ) : (
          <div className={styles.mapFallback}>
            <span role="img" aria-label="Map">🗺️</span>
          </div>
        )}
        {weather && (
          <div className={styles.weatherOverlay}>
            <img
              src={`https://openweathermap.org/img/wn/${weather.weather[0].icon}.png`}
              alt={weather.weather[0].description}
            />
            <span>{Math.round(weather.main.temp)}°C</span>
          </div>
        )}
      </div>

      {/* Modals */}
      {showStartEventModal && programId && programDetails && (
        <StartEventModal
          event={event}
          programScorecard={programDetails.scorecard}
          onClose={() => setShowStartEventModal(false)}
          onEventStarted={(updatedEvent) => {
            setShowStartEventModal(false);
            setShowLiveScorecardModal(true);
            onEventUpdated?.(updatedEvent);
          }}
        />
      )}

      {showLiveScorecardModal && event.status === 'Live' && (
        <LiveScorecardModal
          event={event}
          onClose={() => setShowLiveScorecardModal(false)}
          programSport={programDetails?.sport}
          onEventUpdated={onEventUpdated}
        />
      )}

      {showScoreViewer && event.status === 'Live' && (
        <ReadOnlyScorecard
          event={event}
          onClose={() => setShowScoreViewer(false)}
          onRefresh={onEventUpdated}
        />
      )}

      {showFinalResultModal && event.status === 'Completed' && (
        <FinalResultModal
          event={event}
          onClose={() => setShowFinalResultModal(false)}
        />
      )}

      {/* Event Details Modal */}
      {showDetailsModal && (
        <EventDetailsModal
          event={event}
          onClose={() => setShowDetailsModal(false)}
          weatherData={weatherData}
          canManage={canManage}
          programId={programId}
          programDetails={programDetails}
          onEditEvent={onEditEvent}
          onEventUpdated={onEventUpdated}
        />
      )}
    </div>
  );
};

export default EventCard;
